import * as React from "react";
import { useId, useIndex, useLayer, useOutsideClick } from "../../hooks";
import { PoseGroup } from "react-pose";
import getLayerPosition from "./getLayerPosition";
import composeRefs from "../../util/composeRefs";

import mergeStyles from "../../util/mergeStyles";

const createFakeBox = (width: number, height: number) => ({
  width,
  height,
  top: 0,
  left: 0,
  bottom: height,
  right: width
});

type ChildFunction = (config: {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
}) => React.ReactElement;

export type YPos = "top" | "bottom";
export type XPos = "left" | "right";

export type Position = [XPos, YPos];

export type Positions = {
  layer: Position;
  parent: Position;
};

const defaultPositions: Positions = {
  parent: ["left", "bottom"],
  layer: ["left", "top"]
};

const defaultOffset = {
  x: 0,
  y: 0
};

type Props = {
  children: ChildFunction;
  layer: ((close: () => void) => React.ReactElement) | React.ReactElement;
  positions?: Positions;
  closeOnOutsideClick?: boolean;
  sameWidthAsParent?: boolean;
  offset?: {
    x: number;
    y: number;
  };
  framerWidth?: number;
  framerHeight?: number;
};

function ToggleFloatingLayer({
  framerWidth,
  framerHeight,
  children,
  layer,
  positions = defaultPositions,
  offset = defaultOffset,
  closeOnOutsideClick,
  sameWidthAsParent
}: Props) {
  const id = useId();
  const zIndex = useIndex(id);
  const { updateLayer, removeLayer, parentElement } = useLayer(id);
  const childRef = React.useRef<HTMLElement>();
  const layerRef = React.useRef<HTMLElement>();

  const [isOpen, setOpen] = React.useState(false);

  useOutsideClick([childRef, layerRef], () => {
    if (isOpen && closeOnOutsideClick) {
      setOpen(false);
    }
  });

  React.useEffect(() => {
    const parentBox = parentElement
      ? parentElement.getBoundingClientRect()
      : createFakeBox(framerWidth!, framerHeight!);

    let layerPosition = {
      style: {} as React.CSSProperties,
      translate: { x: "0%", y: "0%" }
    };

    if (isOpen) {
      const triggerBox = childRef.current
        ? childRef.current.getBoundingClientRect()
        : createFakeBox(framerWidth!, framerHeight!);

      layerPosition = getLayerPosition({
        offset,
        parentBox,
        triggerBox,
        positions
      });

      layerPosition.style = {
        position: "absolute",
        zIndex,
        ...layerPosition.style
      };

      if (sameWidthAsParent) {
        layerPosition.style.width = triggerBox.width;
      }
    }

    const content =
      typeof layer === "function" ? layer(() => setOpen(false)) : layer;

    updateLayer(
      <PoseGroup flipMove={false}>
        {isOpen &&
          React.cloneElement(content, {
            key: "layer",
            ref: layerRef,
            style: mergeStyles(content.props.style, layerPosition.style),
            translate: layerPosition.translate
          })}
      </PoseGroup>
    );
  });

  React.useEffect(() => removeLayer, []);

  const render = children({
    isOpen,
    setOpen,
    toggle: () => setOpen(!isOpen)
  });

  return React.cloneElement(render, {
    ref: composeRefs(childRef, (render as any).ref)
  });
}

export default ToggleFloatingLayer;
